import { FC } from 'react';

import classNames from 'classnames';
import useSWR from 'swr';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

import css from './DailyTaskCards.module.css';

import { tournamentsService } from 'v2/api/tournamentsService';
import { ActionsCard } from 'v2/components/actionsCard/ActionsCard';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { Loader } from 'v2/components/loader/Loader';
import { DailyTaskType } from 'v2/constants';
import { DailyTaskModel } from 'v2/types/TaskModel';

interface DailyTaskCardsProps {
  items: DailyTaskModel[];
  cardsTitle: string;
  openEditModal: (id: string) => void;
  openDeleteModal: (id: string) => void;
  isItemsLoading: boolean;
}

interface RenderContentParams {
  items: DailyTaskModel[];
  isItemsLoading: boolean;
  activeTournamentId?: string;
  openEditModal: (id: string) => void;
  openDeleteModal: (id: string) => void;
}

const renderContent = ({
  items,
  isItemsLoading,
  activeTournamentId,
  openEditModal,
  openDeleteModal,
}: RenderContentParams) => {
  if (isItemsLoading) {
    return (
      <Box display="flex" justifyContent="center" width="100%" padding="20px 0">
        <Loader />
      </Box>
    );
  }

  if (items.length === 0) {
    return <EmptyPlaceholder text="There is no item. Please add the first one." />;
  }

  return (
    <Box className={css.dailyTaskCards}>
      {items.map((item, index) => {
        const { id, isSpecial, type, gameId, tournamentId } = item;
        const isActive = !!activeTournamentId && tournamentId === activeTournamentId;

        return (
          <ActionsCard
            key={id}
            size="compact"
            label={`Задание ${index + 1}`}
            openEditModal={() => openEditModal(id)}
            openDeleteModal={() => openDeleteModal(id)}
            deleteConfirmationMessage="Are you sure that you want to delete this daily task?"
            extraActionsCardContainer={classNames(
              isSpecial && css.dailyTaskSpecialCard,
              type === DailyTaskType.TOP_X && css.dailyTaskTopXCard
            )}
          >
            {isActive && <Chip className={css.dailyTaskActiveBadge} label="Active" color="success" size="small" />}
            <Typography className={css.dailyTaskCardText} fontWeight={600} fontSize="13px">
              {gameId}
            </Typography>
            <Typography className={css.dailyTaskCardText} fontWeight={900} fontSize="16px">
              {type}
            </Typography>
          </ActionsCard>
        );
      })}
    </Box>
  );
};

export const DailyTaskCards: FC<DailyTaskCardsProps> = ({
  items,
  cardsTitle,
  openEditModal,
  openDeleteModal,
  isItemsLoading,
}) => {
  const { data: activeTournament } = useSWR('getActiveTournament', tournamentsService.getActiveTournament);

  return (
    <Box className={css.dailyTaskCardsContainer}>
      <Typography fontSize="18px" fontWeight="600">
        {cardsTitle}
      </Typography>

      {renderContent({
        items,
        isItemsLoading,
        activeTournamentId: activeTournament?.id,
        openEditModal,
        openDeleteModal,
      })}
    </Box>
  );
};
